import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { DS2 } from '../../theme/designTokens';
import { acquireScrollLock, releaseScrollLock } from './modalScrollLock';
import { acquireBackgroundInert, releaseBackgroundInert } from './modalInert';
import { useTranslation } from '../../i18n/react';

/**
 * DS2Modal — the canonical modal/dialog shell for the whole application
 * (approved Figma "Frame 100" design). Every true modal renders through this
 * primitive; consumers supply content and actions, never styling.
 *
 * Shell contract:
 *   • portal to document.body — one fixed full-viewport modal root
 *   • backdrop and panel are SIBLING layers: the backdrop blurs + darkens the
 *     entire app (Sidebar, React content, Phaser canvas, chat, footer) while
 *     the panel above it stays perfectly sharp
 *   • dark header (same surface as the desktop Sidebar) with a bold centered
 *     Montserrat title
 *   • light-gray body (#D9D9D9), square top corners, 45px-rounded bottom
 *   • gold / green / red / neutral action variants, 2- or 3-button rows,
 *     stacked vertically on small viewports; the action row is optional for
 *     content dialogs (friend picker, chat) which use `showClose` instead
 *   • focus trap + restoration, aria-labelledby/-describedby, Escape and
 *     backdrop dismissal policies, pending/disabled actions, inline error
 *   • background made inert/aria-hidden and scroll-locked (both
 *     reference-counted) for the modal's whole visible lifetime
 *   • restrained ~180ms entrance AND exit transitions; the node unmounts only
 *     after the exit completes; prefers-reduced-motion closes immediately
 *   • Escape is owned absolutely while open: intercepted at window capture
 *     (before Phaser/gameplay listeners), preventDefault + stopPropagation,
 *     and only then routed through the dismissal policy
 *
 * All measurements/colors come from DS2.modal (theme/designTokens.ts) via the
 * single injected stylesheet below — never duplicate them in a consumer.
 *
 * Business logic (API calls, routing, mode selection) stays in consumers: an
 * action's async work is passed in as `run` and the modal only supplies the
 * pending/error presentation around it.
 */

export type DS2ModalActionVariant = 'gold' | 'green' | 'red' | 'neutral';

export interface DS2ModalAction {
  id: string;
  label: string;
  variant: DS2ModalActionVariant;
  /**
   * Optional async work (e.g. an API call). While it runs every action is
   * disabled, dismissal is blocked and this button shows `pendingLabel`.
   * If it rejects the modal STAYS OPEN, shows the error treatment and
   * re-enables the actions; `onAction` fires only after it resolves.
   */
  run?: () => Promise<void>;
  pendingLabel?: string;
  /** Receives initial focus. Defaults to the first action. */
  autoFocus?: boolean;
  disabled?: boolean;
}

export interface DS2ModalProps {
  open: boolean;
  title: string;
  /** Render the title only for assistive technology (custom header content
   *  such as a player-identity row goes in `icon`). */
  hideTitle?: boolean;
  /** Standard centered description paragraph (aria-describedby). */
  description?: string;
  /** Custom body content, rendered below the description. */
  children?: ReactNode;
  /** Optional — content dialogs (list/chat) may have no action row. */
  actions?: DS2ModalAction[];
  /** Fired after an action completes (immediately when it has no `run`). */
  onAction?: (id: string) => void;
  /**
   * Dismissal handler for Escape / backdrop click / the header close button —
   * should behave like the safe action. Omit to make the modal
   * non-dismissible. Automatically blocked while an action is pending.
   */
  onDismiss?: () => void;
  /** Whether clicking the backdrop dismisses. Default true. */
  dismissOnBackdrop?: boolean;
  /** Show a header ✕ close control (content dialogs without an action row). */
  showClose?: boolean;
  /** Message shown when an action's `run` rejects. Omit to use the shared
   *  localized default (`common:modal.errorGeneric`), resolved at render. */
  errorText?: string;
  /** Optional visual slot above the title, inside the header. */
  icon?: ReactNode;
  /** Overrides the Figma 798px max width (content dialogs are narrower). */
  maxWidth?: number;
  /** Stretch children edge-to-edge and let THEM own scrolling (chat/list). */
  fillBody?: boolean;
  compact?: boolean;
  /**
   * Identity of the current logical request. When it changes while the modal
   * stays open (queued consecutive dialogs), internal pending/error state
   * resets and initial focus re-runs — without unmounting, so the backdrop,
   * scroll lock, and inert background never flicker.
   */
  requestKey?: string | number;
}

const STYLE_ID = 'ds2-modal-styles';

/** The one canonical modal stylesheet, generated from DS2.modal tokens. */
function buildStylesheet(): string {
  const m = DS2.modal;
  const btnVariant = (name: DS2ModalActionVariant) => {
    const v = m.action[name];
    return `.ds2-modal-btn--${name} { background: ${v.background}; border: ${v.border}; box-shadow: ${v.shadow}; color: ${v.text}; }`;
  };
  return `
.ds2-modal-root { position: fixed; inset: 0; z-index: ${m.zIndex}; }
.ds2-modal-root.ds2-modal-exiting { pointer-events: none; }
.ds2-modal-backdrop {
  position: fixed; inset: 0;
  background: ${m.overlayTint};
  -webkit-backdrop-filter: blur(${m.blurPx}px);
  backdrop-filter: blur(${m.blurPx}px);
  opacity: 0;
  transition: opacity ${m.transitionMs}ms ease;
}
@supports not ((backdrop-filter: blur(${m.blurPx}px)) or (-webkit-backdrop-filter: blur(${m.blurPx}px))) {
  .ds2-modal-backdrop { background: ${m.overlayTintFallback}; }
}
.ds2-modal-root.ds2-modal-open .ds2-modal-backdrop { opacity: 1; }
.ds2-modal-center {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
  pointer-events: none;
}
.ds2-modal-panel {
  pointer-events: auto;
  display: flex; flex-direction: column;
  width: min(${m.maxWidth}px, 100%);
  max-height: calc(100vh - 32px);
  max-height: calc(100dvh - 32px);
  border-radius: 0 0 clamp(24px, 5.6vw, ${m.bottomRadius}px) clamp(24px, 5.6vw, ${m.bottomRadius}px);
  overflow: hidden;
  box-shadow: 0 25px 60px -20px rgba(0,0,0,0.8);
  opacity: 0; transform: scale(0.96);
  transition: opacity ${m.transitionMs}ms ease, transform ${m.transitionMs}ms ease;
}
.ds2-modal-root.ds2-modal-open .ds2-modal-panel { opacity: 1; transform: none; }
.ds2-modal-root.ds2-modal-compact .ds2-modal-panel { transform: scale(0.576); }
.ds2-modal-root.ds2-modal-compact.ds2-modal-open .ds2-modal-panel { transform: scale(0.6); }
.ds2-modal-header {
  position: relative;
  flex: none;
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  background: ${m.headerBg};
  padding: clamp(18px, 3vw, 28px) clamp(16px, 4vw, 48px);
}
.ds2-modal-title {
  margin: 0;
  font-family: ${DS2.font.heading};
  font-weight: 700;
  font-size: clamp(24px, 4.2vw, 47px);
  line-height: 1.2;
  letter-spacing: -0.02em;
  text-align: center;
  color: ${m.titleColor};
}
.ds2-modal-title--hidden {
  position: absolute; width: 1px; height: 1px;
  padding: 0; margin: -1px; overflow: hidden;
  clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}
.ds2-modal-close {
  position: absolute; top: 10px; right: 12px;
  display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px;
  background: transparent; border: none; border-radius: 8px;
  color: ${m.titleColor};
  font-size: 24px; line-height: 1;
  cursor: pointer;
}
.ds2-modal-close:focus-visible { outline: 2px solid ${m.titleColor}; outline-offset: 2px; }
.ds2-modal-close:disabled { opacity: 0.5; cursor: default; }
.ds2-modal-body {
  flex: 1 1 auto; min-height: 0;
  display: flex; flex-direction: column;
  background: ${m.bodyBg};
  padding: clamp(20px, 4vw, 35px) clamp(13px, 2vw, 16px) clamp(14px, 2vw, 18px);
}
.ds2-modal-body--fill { padding: 0; }
.ds2-modal-content {
  flex: 1 1 auto; min-height: 0;
  overflow-y: auto;
  display: flex; flex-direction: column; align-items: center;
  width: 100%;
}
.ds2-modal-content--fill {
  align-items: stretch;
  overflow: hidden;
}
.ds2-modal-description {
  margin: 0;
  max-width: 652px;
  font-family: ${DS2.font.body};
  font-weight: 500;
  font-size: clamp(16px, 2.2vw, 25px);
  line-height: 1.6;
  text-align: center;
  color: ${m.bodyText};
  overflow-wrap: break-word;
}
.ds2-modal-error {
  margin: 12px 0 0;
  font-family: ${DS2.font.body};
  font-weight: 500;
  font-size: clamp(13px, 1.6vw, 16px);
  text-align: center;
  color: ${m.errorColor};
}
.ds2-modal-actions {
  flex: none;
  display: flex; align-items: stretch;
  gap: clamp(12px, 2.6vw, ${m.actionGap}px);
  margin-top: clamp(24px, 5vw, 61px);
  width: 100%; max-width: 771px;
  align-self: center;
}
.ds2-modal-actions--three { gap: clamp(10px, 1.8vw, 20px); }
.ds2-modal-btn {
  flex: 1 1 0; min-width: 0;
  min-height: clamp(56px, 9vw, 104px);
  display: flex; align-items: center; justify-content: center;
  padding: 10px 16px;
  border-radius: ${m.buttonRadius}px;
  font-family: ${DS2.font.body};
  font-weight: 500;
  font-size: clamp(14px, 1.9vw, 22px);
  line-height: 1.2;
  letter-spacing: 0.045em;
  text-transform: uppercase;
  text-align: center;
  cursor: pointer;
  transition: filter 150ms ease, opacity 150ms ease;
}
.ds2-modal-actions--three .ds2-modal-btn { font-size: clamp(13px, 1.6vw, 19px); }
.ds2-modal-btn:hover:not(:disabled) { filter: brightness(1.06); }
.ds2-modal-btn:focus-visible { outline: 3px solid ${m.bodyText}; outline-offset: 2px; }
.ds2-modal-btn:disabled { opacity: 0.6; cursor: default; }
${btnVariant('gold')}
${btnVariant('green')}
${btnVariant('red')}
${btnVariant('neutral')}
@media (max-width: 640px) {
  .ds2-modal-actions, .ds2-modal-actions--three { flex-direction: column; }
  .ds2-modal-btn { width: 100%; min-height: 56px; }
}
@media (prefers-reduced-motion: reduce) {
  .ds2-modal-backdrop, .ds2-modal-panel, .ds2-modal-btn { transition: none; }
  .ds2-modal-panel { transform: none; }
}
`;
}

function ensureStylesheet(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = buildStylesheet();
  document.head.appendChild(style);
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

const FOCUSABLE = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/** entering → open → exiting → closed. Rendered whenever not 'closed'. */
type Phase = 'closed' | 'entering' | 'open' | 'exiting';

export function DS2Modal({
  open, title, hideTitle = false, description, children,
  actions = [], onAction, onDismiss, dismissOnBackdrop = true, showClose = false,
  errorText, icon,
  maxWidth, fillBody = false, compact = false, requestKey,
}: DS2ModalProps) {
  const titleId = useId();
  const descId  = useId();
  // Shared close accessible name ("Close"/"Cerrar") — reactive, so a runtime
  // language switch relabels the open modal in place without remounting it.
  const { t: tCommon } = useTranslation('common');
  const panelRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase]         = useState<Phase>(open ? 'entering' : 'closed');
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const pendingRef = useRef<string | null>(null);

  ensureStylesheet();

  // ── Phase machine: entrance and exit transitions ──────────────────────────
  useEffect(() => {
    if (open) {
      setPhase((p) => (p === 'open' || p === 'entering' ? p : 'entering'));
    } else {
      setPhase((p) => (p === 'closed' ? p : 'exiting'));
    }
  }, [open]);

  // entering → open one frame later so the opacity/scale transition runs.
  useEffect(() => {
    if (phase !== 'entering') return;
    if (typeof requestAnimationFrame !== 'function') { setPhase('open'); return; }
    const raf = requestAnimationFrame(() => setPhase('open'));
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  // exiting → closed after the transition (immediately under reduced motion),
  // so the node unmounts only after the exit completes — never a ghost overlay.
  useEffect(() => {
    if (phase !== 'exiting') return;
    if (prefersReducedMotion()) { setPhase('closed'); return; }
    const t = setTimeout(() => setPhase('closed'), DS2.modal.transitionMs);
    return () => clearTimeout(t);
  }, [phase]);

  const active = phase !== 'closed';

  // ── Queued-request handoff: fresh interaction state, same mounted shell ───
  useEffect(() => {
    pendingRef.current = null;
    setPendingId(null);
    setShowError(false);
  }, [requestKey]);

  // ── Scroll lock + background inert + focus save/restore for the whole
  //    visible lifetime (open AND exiting) — released only when fully closed,
  //    so consecutive dialogs never prematurely restore the background. ──────
  useEffect(() => {
    if (!active) return;
    acquireScrollLock();
    acquireBackgroundInert();
    const previouslyFocused = document.activeElement as HTMLElement | null;
    return () => {
      releaseScrollLock();
      releaseBackgroundInert();
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus?.();
      }
    };
  }, [active]);

  // Initial focus: the autoFocus action, else the first enabled focusable.
  // Re-runs per logical request (queued dialogs).
  useEffect(() => {
    if (!active) return;
    const panel = panelRef.current;
    if (!panel) return;
    const buttons = Array.from(panel.querySelectorAll<HTMLButtonElement>('.ds2-modal-btn'));
    const target =
      buttons.find((b) => b.dataset.autofocus === 'true' && !b.disabled) ??
      buttons.find((b) => !b.disabled) ??
      panel.querySelector<HTMLElement>(FOCUSABLE);
    target?.focus();
  }, [active, requestKey]);

  const canDismiss = Boolean(onDismiss) && pendingId === null && phase !== 'exiting';

  // ── Keyboard ownership: WINDOW capture phase so the modal sees keys before
  //    any Phaser/gameplay or background listener (window capture is the very
  //    first stop on the propagation path). Escape is always swallowed while a
  //    modal is visible — dismissal only happens through the policy. ─────────
  useEffect(() => {
    if (!active) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation?.();
        if (canDismiss) onDismiss?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusables.length === 0) { e.preventDefault(); return; }
      const first = focusables[0];
      const last  = focusables[focusables.length - 1];
      const activeEl = document.activeElement as HTMLElement | null;
      if (!activeEl || !panel.contains(activeEl)) {
        e.preventDefault(); first.focus(); return;
      }
      if (e.shiftKey && activeEl === first)      { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && activeEl === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [active, canDismiss, onDismiss]);

  const handleAction = useCallback(async (action: DS2ModalAction) => {
    // Rapid/double clicks while any action is in flight are ignored.
    if (pendingRef.current !== null || action.disabled) return;
    if (!action.run) { onAction?.(action.id); return; }
    pendingRef.current = action.id;
    setPendingId(action.id);
    setShowError(false);
    try {
      await action.run();
      pendingRef.current = null;
      setPendingId(null);
      onAction?.(action.id);
    } catch {
      // Failed action: stay open, show the canonical error, restore buttons.
      pendingRef.current = null;
      setPendingId(null);
      setShowError(true);
    }
  }, [onAction]);

  if (!active) return null;
  if (typeof document === 'undefined') return null;

  // Initial-focus target: the explicit autoFocus action, else the first.
  const explicitFocus = actions.findIndex((a) => a.autoFocus);
  const focusIndex = explicitFocus >= 0 ? explicitFocus : 0;

  const rootClass =
    'ds2-modal-root' +
    (phase === 'open' ? ' ds2-modal-open' : '') +
    (phase === 'exiting' ? ' ds2-modal-exiting' : '') +
    (compact ? ' ds2-modal-compact' : '');

  const node = (
    <div className={rootClass}>
      <div
        className="ds2-modal-backdrop"
        aria-hidden="true"
        onClick={dismissOnBackdrop && canDismiss ? onDismiss : undefined}
      />
      <div className="ds2-modal-center">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={description ? descId : undefined}
          className="ds2-modal-panel"
          style={maxWidth !== undefined ? { width: `min(${maxWidth}px, 100%)` } : undefined}
        >
          <header className="ds2-modal-header">
            {icon}
            <h2 id={titleId} className={hideTitle ? 'ds2-modal-title ds2-modal-title--hidden' : 'ds2-modal-title'}>
              {title}
            </h2>
            {showClose && (
              <button
                type="button"
                className="ds2-modal-close"
                aria-label={tCommon('action.close')}
                disabled={pendingId !== null}
                onClick={canDismiss ? onDismiss : undefined}
              >
                ✕
              </button>
            )}
          </header>
          <div className={fillBody ? 'ds2-modal-body ds2-modal-body--fill' : 'ds2-modal-body'}>
            <div className={fillBody ? 'ds2-modal-content ds2-modal-content--fill' : 'ds2-modal-content'}>
              {description && (
                <p id={descId} className="ds2-modal-description">{description}</p>
              )}
              {children}
              {showError && (
                /* Caller-supplied errorText wins verbatim; otherwise the
                   shared default resolves reactively at render time, so a
                   visible default error relabels in place on a runtime
                   language switch (never stored in state). */
                <p role="alert" className="ds2-modal-error">{errorText ?? tCommon('modal.errorGeneric')}</p>
              )}
            </div>
            {actions.length > 0 && (
              <div className={`ds2-modal-actions${actions.length >= 3 ? ' ds2-modal-actions--three' : ''}`}>
                {actions.map((action, i) => {
                  const isPending = pendingId === action.id;
                  return (
                    <button
                      key={action.id}
                      type="button"
                      className={`ds2-modal-btn ds2-modal-btn--${action.variant}`}
                      data-autofocus={i === focusIndex ? 'true' : 'false'}
                      disabled={pendingId !== null || action.disabled}
                      aria-busy={isPending || undefined}
                      onClick={() => void handleAction(action)}
                    >
                      {isPending ? (action.pendingLabel ?? `${action.label}…`) : action.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <div data-ds2-modal-root="true">{node}</div>,
    document.body,
  );
}
